// Get existing posthog configuration from analytics
import posthog from 'posthog-js';
import { history } from 'umi';
import { navigationService } from '@/common/services/navigation';
import { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import frFR from 'antd/lib/locale/fr_FR';
import { PostHogProvider } from 'posthog-js/react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from '@/common/store';
import CookieConsent from '@/common/components/cookies';
import TodoPopup from '@/common/components/TodoPopup';
import PatrimoinePopup from '@/common/components/PatrimoinePopup';
import SentryErrorBoundary from '@/common/components/SentryErrorBoundary';
import ImpersonationBar from '@/common/components/impersonationBar';

// Sentry imports
import { initSentry, setSentryUser } from '@/common/utils/sentry';

// UTM tracking
import { initUtmTracking } from '@/common/utils/utm';

// Initialize Sentry
initSentry();

// Initialize UTM tracking
initUtmTracking();

const posthogKey = 'phc_23Ull9OUenLSIiSOVFtN8aTgAcovuvyqoPMDpZG9DyX';

// Initialize Google Analytics (GA4)
if (process.env.NODE_ENV !== 'development' && typeof window !== 'undefined') {
  // Add Google Analytics tracking script
  const script = document.createElement('script');
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-301LS1XW9Y';
  script.async = true;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());

  // Check for existing consent
  const cookieConsent = localStorage.getItem('cookieConsent');
  const hasConsent =
    cookieConsent === 'accepted' || cookieConsent === undefined;

  gtag('config', 'G-301LS1XW9Y', {
    consent_mode: 'default',
    analytics_storage: hasConsent ? 'granted' : 'denied',
  });

  // Track page views
  if (history && history.listen) {
    history.listen(({ pathname }) => {
      gtag('event', 'page_view', {
        page_path: pathname,
      });
    });
  }
}

// REMOVE all session recording specific code
export function render(oldRender) {
  // Initialize UTM tracking here when DOM is ready
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      initUtmTracking();
    }, 100);
  }

  const token = window.localStorage.getItem('token');

  // url that don't need to check token
  const whiteList = [
    '/register',
    '/resetPassword',
    '/diagnosis',
    '/legalForm',
    '/share',
    '/unsubscribe',
    '/login',
    '/auth/google/callback',
    '/auth/callback',
    '/impersonate',
  ];
  if (!token) {
    // Reset analytics when user is not logged in
    if (process.env.NODE_ENV !== 'development') {
      posthog.reset();
    }

    if (!whiteList.includes(history?.location?.pathname || window.location.pathname)) {
      const from = window.location.pathname;
      if ((history?.location?.pathname || window.location.pathname) == '/') {
        navigationService.replace(`/login`);
      } else {
        navigationService.replace(`/login?from=${encodeURIComponent(from)}`);
      }
    }
  } else {
    const userInfo = localStorage.getItem('userInfo');
    const isAccepts = window.localStorage.getItem('isAccept');
    const token = window.localStorage.getItem('token');
    const diagnosisFormDataIsEnd = window.localStorage.getItem(
      'diagnosisFormDataIsEnd',
    );
    const leagalFormDataIsEnd = window.localStorage.getItem(
      'leagalFormDataIsEnd',
    );
    if (!isAccepts == '1' || isAccepts == '0') {
      window.localStorage.setItem('diagnosisFormDataIsEnd', false);
    }

    if (userInfo && token) {
      const _userInfo = JSON.parse(userInfo);
      console.log('userInfo', _userInfo);
      const { has_diagnosis, has_legal, id, email } = _userInfo;

      // Identify user in PostHog
      if (id && process.env.NODE_ENV !== 'development') {
        posthog.identify(id, {
          email,
          has_diagnosis,
          has_legal,
          account_type: _userInfo?.account_type || 'standard',
          signup_date: _userInfo?.created_at || '',
          last_login: new Date().toISOString(),
        });

        // Identify user in Sentry
        setSentryUser({
          id,
          email,
          account_type: _userInfo?.account_type || 'standard',
          has_diagnosis,
          has_legal,
        });
      }

      if (
        has_diagnosis !== 1 &&
        has_legal !== 1 &&
        diagnosisFormDataIsEnd !== 'true'
      ) {
        navigationService.push('/diagnosis');
      } else if (
        has_diagnosis == 1 &&
        has_legal !== 1 &&
        leagalFormDataIsEnd !== 'true'
      ) {
        navigationService.push('/legalForm');
      }
    } else {
      // if(diagnosisFormDataIsEnd == 'true' && leagalFormDataIsEnd == 'false') {
      //   navigationService.push('/legalForm');
      // } else if(diagnosisFormDataIsEnd == 'true' && leagalFormDataIsEnd == 'true'){
      //   navigationService.push('/legalForm');
      // } else {
      //   navigationService.push('/diagnosis');
      // }
    }
  }
  oldRender();
}

// Component to handle route-based redirects
function RouteGuard({ children }) {
  useEffect(() => {
    const handleRouteChange = () => {
      const token = window.localStorage.getItem('token');
      if (!token) return;

      const userInfo = localStorage.getItem('userInfo');
      if (!userInfo) return;

      const _userInfo = JSON.parse(userInfo);
      const path = history?.location?.pathname || window.location.pathname;

      // Check subscription path and redirect if needed
      if (
        _userInfo.subscription_path &&
        _userInfo.subscription_path !== 'free' &&
        !_userInfo.has_completed_legal &&
        !path.includes('/home/updatePlan') &&
          !path.includes('/home/reglementation')
      ) {
        const type = _userInfo.subscription_path === 'dueOrder' ? 2 : 3;
        navigationService.push(`/home/updatePlan?type=${type}&status=success`);
      }
    };

    // Check on mount
    handleRouteChange();

    // Listen for route changes
    const unlisten = history && history.listen ? history.listen(handleRouteChange) : () => {};

    return () => unlisten();
  }, []);

  return children;
}

export function rootContainer(container) {
  if (process.env.NODE_ENV === 'development') {
    return (
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <SentryErrorBoundary>
            <ConfigProvider locale={frFR}>
              <RouteGuard>
                <ImpersonationBar />
                {container}
                <TodoPopup />
                <PatrimoinePopup />
              </RouteGuard>
            </ConfigProvider>
          </SentryErrorBoundary>
        </PersistGate>
      </Provider>
    );
  }

  const cookieConsent = localStorage.getItem('cookieConsent');
  const hasConsent =
    cookieConsent === 'accepted' || cookieConsent === undefined;

  const posthogOptions = {
    api_host: 'https://eu.i.posthog.com',
    opt_in_site_apps: hasConsent,
    autocapture: hasConsent,
    capture_pageview: hasConsent,
  };

  if (!hasConsent) {
    posthog.opt_out_capturing();
  }

  return (
    <>
      {/* Ajout du Google Tag Manager noscript */}
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=G-301LS1XW9Y"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        ></iframe>
      </noscript>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
            <SentryErrorBoundary>
              <ConfigProvider locale={frFR}>
                <RouteGuard>
                  <ImpersonationBar />
                  {container}
                  <TodoPopup />
                  <PatrimoinePopup />
                </RouteGuard>
              </ConfigProvider>
            </SentryErrorBoundary>
            <CookieConsent />
          </PostHogProvider>
        </PersistGate>
      </Provider>
    </>
  );
}
